import React, { useState } from "react";
import {
  Typography,
  Input,
  Button,
  Form,
  Card,
  message,
  Alert,
  Space,
  Divider,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { sha256 } from "hash.js";
import { useLocation } from "react-router-dom";
import { firebaseAPI } from "../firebaseConfig";
import axiosInstance from "../utils/axiosInstance";
import { useEffect } from "react";

// Function to hash a string using SHA-256
function hashString(input) {
  return sha256().update(input).digest("hex");
}

const { Title, Text } = Typography;

const SignUpSignInPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isSignUp, setIsSignUp] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const allowsSignUp = false;
  const [program, setProgram] = useState(
    decodeURI(location.pathname.split("/")[1])
  );
  useEffect(() => {
    if (!allowsSignUp && isSignUp) {
      setIsSignUp(false);
    }
  }, [allowsSignUp, isSignUp]);

  const handleSubmit = async () => {
    if (!email || !password) {
      message.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      let response;
      if (isSignUp) {
        response = await axios.post(`${firebaseAPI}/signup`, {
          name,
          email,
          password,
        });
      } else {
        response = await axios.post(`${firebaseAPI}/signin`, {
          email,
          password,
          programName: program,
        });
      }

      const accessToken = response.data.token;
      const refreshToken = response.data.refreshToken;

      const expirationTime = new Date();
      expirationTime.setMinutes(expirationTime.getMinutes() + 15); // short expiry

      Cookies.set("token", accessToken, {
        expires: expirationTime,
        secure: true,
      });

      localStorage.setItem("refreshToken", refreshToken); // store refresh token

      Cookies.set("role", hashString(response.data.role), { secure: true });
      Cookies.set("tokenExpiration", expirationTime);

      message.success(isSignUp ? "Sign up successful" : "Sign in successful");

      const roleRedirects = {
        participant: `/${program}/participantprofile`,
        judge: `/${program}/judgehome`,
        admin: `/${program}/overview`,
      };

      window.location.href = roleRedirects[response.data.role] || `/${program}`;
    } catch (error) {
      console.error("Error:", error);
      message.error(error.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      message.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(
        `${firebaseAPI}/forgotPassword`,
        {
          email,
        }
      );
      message.success({
        content:
          "Your reset request has been received. Please allow some time for the reset link to be sent.",
        duration: 10, // seconds
      });
      setShowForgotPassword(false);
    } catch (error) {
      console.error("Error:", error);
      message.error("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    width: "100%",
    maxWidth: "450px",
    padding: "40px 30px",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(0, 39, 102, 0.12)",
    border: "1px solid rgba(0, 39, 102, 0.08)",
    background: "#ffffff",
  };

  const containerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    padding: "20px",
    backgroundColor: "#ffffff",
  };

  const titleStyle = {
    textAlign: "center",
    marginBottom: "30px",
    color: "#002766",
    fontWeight: "600",
    textShadow: "0 1px 2px rgba(0,0,0,0.1)",
  };

  const inputStyle = {
    height: "45px",
    borderRadius: "8px",
    fontSize: "14px",
  };

  const buttonStyle = {
    height: "45px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    width: "100%",
    backgroundColor: "#1890ff",
    borderColor: "#1890ff",
    color: "#ffffff",
  };

  return (
    <div style={containerStyle}>
      <Card style={cardStyle} bordered={false}>
        {!allowsSignUp && !isSignUp && (
          <Alert
            message="Applications Closed"
            description="The deadline for sign-up has passed. Applications are no longer being accepted at this time."
            type="warning"
            showIcon
            style={{
              marginBottom: "20px",
              borderRadius: "8px",
              backgroundColor: "#fffbe6",
            }}
          />
        )}
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <Title level={2} style={titleStyle}>
            {showForgotPassword
              ? "Reset Password"
              : isSignUp
              ? "Create Account"
              : "Welcome Back"}
          </Title>
          {!showForgotPassword && (
            <Text type="secondary" style={{ fontSize: "14px" }}>
              {isSignUp
                ? "Join us today and get started"
                : "Sign in to your account"}
            </Text>
          )}
        </div>
        {/* Sign Up Alert */}
        {allowsSignUp && isSignUp && !showForgotPassword && (
          <Alert
            message="Important: Email Verification Required"
            description="Please ensure you use a valid email address that you check regularly. You will receive important notifications and verification emails at this address."
            type="info"
            icon={<InfoCircleOutlined />}
            showIcon
            style={{
              marginBottom: "25px",
              borderRadius: "8px",
              border: "1px solid #d9f7be",
              backgroundColor: "#f6ffed",
            }}
          />
        )}
        <Form
          onFinish={handleSubmit}
          layout="vertical"
          size="large"
          style={{ marginTop: "10px" }}
        >
          {showForgotPassword ? (
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Text type="secondary">
                  Enter your email address and we'll send you a link to reset
                  your password.
                </Text>
              </div>

              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please enter your email address",
                    type: "email",
                  },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: "#666" }} />}
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                />
              </Form.Item>

              <Button
                type="primary"
                onClick={handleForgotPassword}
                loading={loading}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#1890ff",
                  borderColor: "#1890ff",
                  color: "#ffffff",
                }}
              >
                Send Reset Link
              </Button>

              <div style={{ textAlign: "center" }}>
                <Button
                  type="link"
                  onClick={() => setShowForgotPassword(false)}
                  style={{ padding: 0, fontSize: "14px", color: "#1890ff" }}
                >
                  ← Back to Sign In
                </Button>
              </div>
            </Space>
          ) : (
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              {allowsSignUp && isSignUp && (
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter your full name" },
                    { min: 2, message: "Name must be at least 2 characters" },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: "#666" }} />}
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={inputStyle}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please enter a valid email address",
                    type: "email",
                  },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: "#666" }} />}
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Please enter your password" },
                  allowsSignUp &&
                    isSignUp && {
                      min: 6,
                      message: "Password must be at least 6 characters",
                    },
                ].filter(Boolean)}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "#666" }} />}
                  placeholder={
                    isSignUp
                      ? "Create a strong password"
                      : "Enter your password"
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={inputStyle}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#1890ff",
                  borderColor: "#1890ff",
                  color: "#ffffff",
                }}
              >
                {isSignUp ? "Create Account" : "Sign In"}
              </Button>

              <div style={{ textAlign: "center" }}>
                <Space direction="vertical" size="small">
                  <Text style={{ fontSize: "14px" }}>
                    {isSignUp ? (
                      <>
                        <Divider style={{ margin: "20px 0" }}>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            OR
                          </Text>
                        </Divider>
                        Already have an account?{" "}
                        <Button
                          type="link"
                          onClick={() => setIsSignUp(false)}
                          style={{
                            padding: 0,
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#1890ff",
                          }}
                        >
                          Sign In
                        </Button>
                      </>
                    ) : (
                      <>
                        {allowsSignUp && (
                          <>
                            <Text> Don't have an account? </Text>
                            <Button
                              type="link"
                              onClick={() => setIsSignUp(true)}
                              style={{
                                padding: 0,
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#1890ff",
                              }}
                            >
                              Sign Up
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </Text>

                  {!isSignUp && (
                    <Button
                      type="link"
                      onClick={() => setShowForgotPassword(true)}
                      style={{ padding: 0, fontSize: "13px", color: "#1890ff" }}
                    >
                      Forgot your password?
                    </Button>
                  )}
                </Space>
              </div>
            </Space>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default SignUpSignInPage;
